/**
 * Monthly Revive Nights schedule.
 * `date` is an ISO date string for accurate sorting / "next event" detection.
 * `tbd: true` events have no firm date yet — show in the strip but disable details.
 *
 * To add or update: edit the array. The strip auto-sorts by date and auto-skips
 * past months so the next upcoming experience always leads.
 */
export const REVIVE_NIGHTS = [
  {
    id: 'may-2026',
    monthLabel: 'May',
    edition: 'May Edition',
    theme: 'In Spirit, In Truth',
    verseRef: 'John 4:24, NIV',
    verseText:
      'God is spirit, and his worshipers must worship in the Spirit and in truth.',
    date: '2026-05-29',
    timeLabel: '6:00 PM',
    locationName: 'CoHatch SouthSide Works',
    locationShort: 'CoHatch',
    locationUrl:
      'https://www.google.com/maps/search/?api=1&query=CoHatch+SouthSide+Works+Pittsburgh',
    image: '/images/strip-1.jpg',
    tbd: false,
  },
  {
    id: 'june-2026',
    monthLabel: 'June',
    edition: 'June Edition',
    theme: 'TBD',
    verseRef: '',
    verseText: '',
    date: '2026-06-20',
    timeLabel: '6:00 PM',
    locationName: 'CoHatch SouthSide Works · Southside, Pittsburgh',
    locationShort: 'CoHatch',
    locationUrl:
      'https://www.google.com/maps/search/?api=1&query=CoHatch+SouthSide+Works+Pittsburgh',
    image: '/images/strip-2.jpg',
    tbd: false,
  },
  {
    id: 'july-2026',
    monthLabel: 'July',
    edition: 'July: Sabbath',
    theme: 'Rest',
    verseRef: 'Hebrews 4:9-10, NIV',
    verseText:
      'There remains, then, a Sabbath-rest for the people of God; for anyone who enters God’s rest also rests from their works, just as God did from his.',
    date: '2026-07-31',
    timeLabel: 'A month of pause',
    locationName: 'No public gathering',
    locationUrl: '',
    image: '/images/strip-3.jpg',
    tbd: false,
    isSabbath: true,
  },
  {
    id: 'august-2026',
    monthLabel: 'August',
    edition: 'August Edition',
    theme: 'Unashamed',
    verseRef: 'Romans 1:16, NIV',
    verseText:
      'For I am not ashamed of the gospel, because it is the power of God that brings salvation to everyone who believes.',
    date: '2026-08-22',
    timeLabel: '6:00 PM',
    locationName: 'Schenley Plaza',
    locationShort: 'Schenley Plaza',
    locationUrl:
      'https://www.google.com/maps/search/?api=1&query=Schenley+Plaza+Pittsburgh',
    image: '/images/strip-4.jpg',
    tbd: false,
  },
  {
    id: 'september-2026',
    monthLabel: 'September',
    edition: 'September Edition',
    theme: 'TBD',
    verseRef: '',
    verseText: '',
    date: '2026-09-19',
    timeLabel: '6:30 PM',
    locationName:
      'Simmons Auditorium · Tepper School of Business, Carnegie Mellon University',
    locationShort: 'Carnegie Mellon',
    locationUrl:
      'https://www.google.com/maps/search/?api=1&query=Tepper+School+of+Business+Carnegie+Mellon+University+Pittsburgh',
    image: '/images/strip-5.jpg',
    tbd: false,
  },
  {
    id: 'october-2026',
    monthLabel: 'October',
    edition: 'October Edition',
    theme: 'TBD',
    verseRef: '',
    verseText: '',
    date: '2026-10-31',
    timeLabel: 'Time TBD',
    locationName: 'Africa Union Ballroom · Duquesne University',
    locationShort: 'Duquesne',
    locationUrl:
      'https://www.google.com/maps/search/?api=1&query=Duquesne+University+Union+Pittsburgh',
    image: '/images/strip-6.jpg',
    tbd: false,
  },
  {
    id: 'november-2026',
    monthLabel: 'November',
    edition: 'November Edition',
    theme: 'TBD',
    verseRef: '',
    verseText: 'Details still being prayed in. Follow along for the drop.',
    date: '2026-11-30',
    timeLabel: 'TBD',
    locationName: 'TBD',
    locationUrl: '',
    image: '/images/strip-7.jpg',
    tbd: true,
  },
  {
    id: 'december-2026',
    monthLabel: 'December',
    edition: 'December Edition',
    theme: 'TBD',
    verseRef: '',
    verseText: 'Details still being prayed in. Follow along for the drop.',
    date: '2026-12-31',
    timeLabel: 'TBD',
    locationName: 'TBD',
    locationUrl: '',
    image: '/images/crowd-bw.jpg',
    tbd: true,
  },
];

/**
 * Parse an event-date string ('YYYY-MM-DD') as LOCAL midnight, not UTC.
 * Using `new Date('2026-05-29')` parses as UTC, which in US timezones lands
 * the previous calendar day in local time — and would make events appear
 * to expire one day early. This function avoids that.
 */
function parseEventDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Return events filtered + sorted with the next upcoming first.
 * An event remains visible *through* its day (so a May 29 event is still
 * "next" all day on May 29; on May 30 it rolls off and June becomes next).
 * `now` is injectable so unit tests / SSR can pin time.
 */
export function getUpcomingNights(now = new Date(), nights = REVIVE_NIGHTS) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return [...(nights || REVIVE_NIGHTS)]
    .filter((e) => parseEventDate(e.date) >= today)
    .sort((a, b) => parseEventDate(a.date) - parseEventDate(b.date));
}

export function getNextNight(now = new Date(), nights = REVIVE_NIGHTS) {
  return getUpcomingNights(now, nights)[0] || null;
}

export function formatLongDate(iso) {
  if (!iso) return '';
  return parseEventDate(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatShortDate(iso) {
  if (!iso) return '';
  return parseEventDate(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/** 'Saturday, June 20' — for season listings where the year is implied. */
export function formatDayDate(iso) {
  if (!iso) return '';
  return parseEventDate(iso).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * One-line date+time for listings.
 * 'Saturday, June 20 · 6:00 PM' / 'Saturday, October 31 · Time TBD' /
 * 'Date & time TBD' / 'All month' (sabbath).
 */
export function listingDateLabel(event) {
  if (event.isSabbath) return 'All month';
  if (event.tbd) return 'Date & time TBD';
  return `${formatDayDate(event.date)} · ${event.timeLabel}`;
}

/**
 * True only when an event has a real announced theme. 'TBD' is a data
 * sentinel, never copy — events without a theme simply don't show one.
 */
export function hasTheme(event) {
  return Boolean(event.theme) && event.theme !== 'TBD';
}

/**
 * Whole days until the event (0 = today). Null for TBD-dated events.
 * Used for the live countdown on the Up Next surfaces.
 */
export function daysUntil(event, now = new Date()) {
  if (!event || event.tbd || event.isSabbath) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((parseEventDate(event.date) - today) / 86400000);
}

/** "Tonight" / "Tomorrow" / "In 10 days" — or '' when not applicable. */
export function countdownLabel(event, now = new Date()) {
  const d = daysUntil(event, now);
  if (d === null || d < 0) return '';
  if (d === 0) return 'Tonight';
  if (d === 1) return 'Tomorrow';
  return `In ${d} days`;
}
